/**
 * Income Statement (P&L) — cash-basis view.
 * Gated under the 'pulse' permission.
 *
 * Sources:
 *   Revenue  = Chase customer credits
 *   COGS     = Chase carrier debits
 *   OpEx     = Chase ops/affiliate/fee + operating_costs table
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { fmtMoney, type Currency } from '../lib/money'
import { useCurrency } from '../lib/currency-context'
import { useAuth, authHeaders } from '../lib/auth'
import { BarChart } from '../components/charts/BarChart'
import { useTranslation } from 'react-i18next'

// ── Period options ────────────────────────────────────────────────────────────

const PERIODS = [
  { key: 'this_month',   label: 'This month' },
  { key: 'last_month',   label: 'Last month' },
  { key: 'this_quarter', label: 'This quarter' },
  { key: 'last_quarter', label: 'Last quarter' },
  { key: 'ytd',          label: 'Year to date' },
  { key: 'last_year',    label: 'Last year' },
]

// ── Types ─────────────────────────────────────────────────────────────────────

interface OpEx {
  personnel_cents:      number
  infrastructure_cents: number
  ops_other_cents:      number
  affiliates_cents:     number
  banking_cents:        number
  manual_cents:         number
  total_cents:          number
}

interface IncomeData {
  period:              { from: string; to: string; label: string }
  revenue_cents:       number
  cogs_cents:          number
  gross_profit_cents:  number
  gross_margin_pct:    number
  opex:                OpEx
  net_profit_cents:    number
  net_margin_pct:      number
}

interface TrendRow {
  month:              string
  revenue_cents:      number
  cogs_cents:         number
  gross_profit_cents: number
  opex_cents:         number
  net_profit_cents:   number
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function pct(value: number, total: number): string {
  if (!total) return '—'
  return (Math.round(value / total * 1000) / 10).toFixed(1) + '%'
}

function marginClass(cents: number): string {
  if (cents > 0) return 'text-mint-600'
  if (cents < 0) return 'text-flame-600'
  return 'text-ink-500'
}

// ── P&L row component ─────────────────────────────────────────────────────────

interface RowProps {
  label:       string
  cents:       number
  revenue:     number
  currency:    Currency
  indent?:     boolean
  bold?:       boolean
  separator?:  boolean
  dimmed?:     boolean
  colored?:    boolean
}

function PnlRow({ label, cents, revenue, currency, indent, bold, separator, dimmed, colored }: RowProps) {
  const labelCls = [
    'py-2 pr-4',
    indent ? 'pl-8 text-[12px] text-ink-600' : 'pl-2 text-[13px]',
    bold ? 'font-semibold text-ink-900' : '',
    dimmed ? 'text-ink-400' : '',
  ].filter(Boolean).join(' ')

  const valCls = [
    'py-2 text-right tabular-nums text-[13px]',
    bold ? 'font-semibold' : '',
    colored ? marginClass(cents) : '',
    dimmed ? 'text-ink-400' : '',
  ].filter(Boolean).join(' ')

  return (
    <>
      {separator && <tr><td colSpan={3} className="border-t border-ink-200 py-0" /></tr>}
      <tr className="hover:bg-ink-50/50">
        <td className={labelCls}>{label}</td>
        <td className={valCls}>{fmtMoney(cents, currency)}</td>
        <td className="py-2 pl-3 text-right text-[11px] text-ink-400 tabular-nums w-16">
          {indent ? pct(Math.abs(cents), revenue) : ''}
        </td>
      </tr>
    </>
  )
}

// ── Main view ─────────────────────────────────────────────────────────────────

export function IncomeView() {
  const { t } = useTranslation()
  const { currency }    = useCurrency()
  const { token }       = useAuth()
  const [period, setPeriod] = useState('this_quarter')

  const income = useQuery<IncomeData>({
    queryKey: ['income', period],
    queryFn:  () => fetch(`/api/income?period=${period}`, { headers: authHeaders(token) })
                      .then(r => r.json()),
  })

  const trend = useQuery<{ rows: TrendRow[] }>({
    queryKey: ['income', 'trend'],
    queryFn:  () => fetch('/api/income/trend?months=12', { headers: authHeaders(token) })
                      .then(r => r.json()),
  })

  const d = income.data

  // Trend chart data
  const trendRows = trend.data?.rows ?? []
  const trendLabels = trendRows.map(r => r.month)
  const trendSeries = [
    { label: t('income.revenue'),      data: trendRows.map(r => r.revenue_cents / 100),      color: '#6366f1' },
    { label: t('income.gross_profit'), data: trendRows.map(r => r.gross_profit_cents / 100), color: '#10b981' },
    { label: t('income.net_profit'),   data: trendRows.map(r => r.net_profit_cents / 100),   color: '#0ea5e9' },
  ]

  const periodLabel = PERIODS.find(p => p.key === period)?.label ?? period

  return (
    <div className="space-y-5">

      {/* Header + period picker */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-[20px] font-bold text-ink-900">{t('income.title')}</h1>
          <p className="text-[12px] text-ink-500 mt-0.5">
            {t('income.subtitle')}
          </p>
        </div>
        <select
          value={period}
          onChange={e => setPeriod(e.target.value)}
          className="px-3 py-1.5 border border-ink-200 rounded-lg text-sm bg-white text-ink-700 font-medium"
        >
          {PERIODS.map(p => <option key={p.key} value={p.key}>{p.label}</option>)}
        </select>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">{t('income.revenue')}</div>
          <div className="mt-2 text-[26px] font-bold text-ink-900">
            {d ? fmtMoney(d.revenue_cents, currency, { compact: true }) : '—'}
          </div>
          <div className="text-[11px] text-ink-400 mt-1">{periodLabel}</div>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">{t('income.gross_profit')}</div>
          <div className={`mt-2 text-[26px] font-bold ${d ? marginClass(d.gross_profit_cents) : 'text-ink-300'}`}>
            {d ? fmtMoney(d.gross_profit_cents, currency, { compact: true }) : '—'}
          </div>
          <div className="text-[11px] text-ink-400 mt-1">
            {d ? `${d.gross_margin_pct}% margin` : '—'}
          </div>
        </Card>
        <Card className="p-5">
          <div className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">{t('income.net_profit')}</div>
          <div className={`mt-2 text-[26px] font-bold ${d ? marginClass(d.net_profit_cents) : 'text-ink-300'}`}>
            {d ? fmtMoney(d.net_profit_cents, currency, { compact: true }) : '—'}
          </div>
          <div className="text-[11px] text-ink-400 mt-1">
            {d ? `${d.net_margin_pct}% margin` : '—'}
          </div>
        </Card>
      </div>

      {/* P&L statement table */}
      <Card className="p-5">
        <div className="text-[14px] font-semibold text-ink-800 mb-3">
          P&amp;L — {periodLabel}
          {d?.period && (
            <span className="text-[11px] font-normal text-ink-400 ml-2">
              {d.period.from} → {d.period.to}
            </span>
          )}
        </div>

        {income.isLoading ? (
          <div className="text-center py-8 text-ink-400 text-[13px]">{t('common.loading')}</div>
        ) : !d?.revenue_cents && d?.revenue_cents !== 0 ? (
          <div className="text-center py-8 text-ink-400 text-[13px]">{t('common.no_data')}</div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-ink-400 border-b border-ink-200">
                <th className="text-left pb-2 pl-2">{t('income.line_item')}</th>
                <th className="text-right pb-2">{t('income.amount')}</th>
                <th className="text-right pb-2 pl-3 w-16">{t('income.pct_rev')}</th>
              </tr>
            </thead>
            <tbody>
              {/* Revenue */}
              <PnlRow label={t('income.revenue')} cents={d.revenue_cents} revenue={d.revenue_cents} currency={currency} bold />

              {/* COGS */}
              <PnlRow label={t('income.cost_of_revenue')} cents={-d.cogs_cents} revenue={d.revenue_cents} currency={currency} separator />
              <PnlRow label={t('income.carrier_payments')} cents={-d.cogs_cents} revenue={d.revenue_cents} currency={currency} indent />

              {/* Gross profit */}
              <PnlRow label={t('income.gross_profit')} cents={d.gross_profit_cents} revenue={d.revenue_cents} currency={currency} bold separator colored />

              {/* OpEx */}
              <PnlRow label={t('income.operating_expenses')} cents={-d.opex.total_cents} revenue={d.revenue_cents} currency={currency} separator />
              {d.opex.personnel_cents > 0 && (
                <PnlRow label={t('income.personnel')} cents={-d.opex.personnel_cents} revenue={d.revenue_cents} currency={currency} indent />
              )}
              {d.opex.infrastructure_cents > 0 && (
                <PnlRow label={t('income.infrastructure')} cents={-d.opex.infrastructure_cents} revenue={d.revenue_cents} currency={currency} indent />
              )}
              {d.opex.affiliates_cents > 0 && (
                <PnlRow label={t('income.affiliate_payouts')} cents={-d.opex.affiliates_cents} revenue={d.revenue_cents} currency={currency} indent />
              )}
              {d.opex.banking_cents > 0 && (
                <PnlRow label={t('income.banking_fees')} cents={-d.opex.banking_cents} revenue={d.revenue_cents} currency={currency} indent />
              )}
              {d.opex.ops_other_cents > 0 && (
                <PnlRow label={t('income.other_operations')} cents={-d.opex.ops_other_cents} revenue={d.revenue_cents} currency={currency} indent />
              )}
              {d.opex.manual_cents > 0 && (
                <PnlRow label={t('income.additional_costs')} cents={-d.opex.manual_cents} revenue={d.revenue_cents} currency={currency} indent />
              )}

              {/* Net profit */}
              <PnlRow label={t('income.net_profit')} cents={d.net_profit_cents} revenue={d.revenue_cents} currency={currency} bold separator colored />
            </tbody>
          </table>
        )}
      </Card>

      {/* 12-month trend */}
      <Card className="p-5">
        <div className="text-[14px] font-semibold text-ink-800 mb-4">
          {t('income.twelve_month_trend')}
        </div>
        {trend.isLoading ? (
          <div className="text-center py-8 text-ink-400 text-[13px]">{t('common.loading')}</div>
        ) : trendRows.length === 0 ? (
          <div className="text-center py-8 text-ink-400 text-[13px]">{t('income.no_trend_data')}</div>
        ) : (
          <BarChart
            labels={trendLabels}
            series={trendSeries}
            height={260}
            stacked={false}
            yTickFormat={v => `$${Math.round(v / 1000)}k`}
          />
        )}
      </Card>

    </div>
  )
}
